import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { wheelOfLifeQuestions } from '../data/questions';
import { CheckCircle2 } from 'lucide-react';

const OPTIONS = [
    { score: 0, label: "Not at all true" },
    { score: 1, label: "Slightly true" },
    { score: 2, label: "Moderately true" },
    { score: 3, label: "Mostly true" },
    { score: 4, label: "Completely true" },
];

const AssessmentForm = ({ onComplete }) => {
    const [currentIdx, setCurrentIdx] = useState(0);
    const [responses, setResponses] = useState({});
    const [direction, setDirection] = useState(1); // 1 for next, -1 for prev

    const [isNavigating, setIsNavigating] = useState(false);

    const currentQuestion = wheelOfLifeQuestions[currentIdx];

    if (!currentQuestion) return null;

    const handleAnswer = (score) => {
        if (isNavigating) return;

        const newResponses = { ...responses, [currentQuestion.id]: score };
        setResponses(newResponses);

        if (currentIdx < wheelOfLifeQuestions.length - 1) {
            setIsNavigating(true);
            setDirection(1);
            setTimeout(() => {
                setCurrentIdx(prev => prev + 1);
                setIsNavigating(false);
            }, 250); // slight delay for visual feedback
        } else {
            finishAssessment(newResponses);
        }
    };

    const finishAssessment = (finalResponses) => {
        // Calculate category scores
        const scores = {};
        const categories = [...new Set(wheelOfLifeQuestions.map(q => q.category))];

        categories.forEach(cat => {
            scores[cat] = 0;
        });

        wheelOfLifeQuestions.forEach(q => {
            const score = finalResponses[q.id] || 0;
            if (scores[q.category] !== undefined) {
                scores[q.category] += score;
            }
        });

        onComplete(scores);
    };

    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? 50 : -50,
            opacity: 0,
            scale: 0.95
        }),
        center: {
            x: 0,
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.4,
                ease: "easeOut"
            }
        },
        exit: (direction) => ({
            x: direction > 0 ? -50 : 50,
            opacity: 0,
            scale: 0.95,
            transition: {
                duration: 0.3,
                ease: "easeIn"
            }
        })
    };

    return (
        <div className="w-full max-w-2xl mx-auto">
            {/* Progress Bar */}
            <div className="mb-8">
                <div className="flex justify-between text-xs font-semibold text-stone-500 mb-2 uppercase tracking-wider">
                    <span>Question {currentIdx + 1} of {wheelOfLifeQuestions.length}</span>
                    <span>{currentQuestion.category}</span>
                </div>
                <div className="h-2 w-full bg-stone-200 rounded-full overflow-hidden">
                    <motion.div
                        className="h-full bg-earth-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${((currentIdx + 1) / wheelOfLifeQuestions.length) * 100}%` }}
                        transition={{ duration: 0.5 }}
                    />
                </div>
            </div>

            <div className="relative min-h-[400px]">
                <AnimatePresence initial={false} mode="wait" custom={direction}>
                    <motion.div
                        key={currentIdx}
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        className="card-premium absolute w-full"
                    >
                        <div className="mb-8 text-center">
                            <span className="inline-block px-3 py-1 bg-earth-100 text-earth-700 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                                {currentQuestion.category}
                            </span>
                            <h2 className="text-2xl font-display font-medium text-earth-900 leading-relaxed">
                                {currentQuestion.text}
                            </h2>
                        </div>

                        <div className="space-y-3">
                            {OPTIONS.map((option) => (
                                <button
                                    key={option.score}
                                    onClick={() => handleAnswer(option.score)}
                                    disabled={isNavigating}
                                    className={`w-full group text-left px-6 py-4 rounded-xl border transition-all duration-200 flex items-center justify-between
                                        ${isNavigating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-earth-50 hover:border-earth-200 cursor-pointer'}
                                        ${responses[currentQuestion.id] === option.score
                                            ? 'border-earth-500 bg-earth-50'
                                            : 'border-stone-200 bg-white/50'
                                        }
                                    `}
                                >
                                    <span className={`font-medium transition-colors ${responses[currentQuestion.id] === option.score ? 'text-earth-700' : 'text-stone-700 group-hover:text-earth-700'}`}>
                                        {option.label}
                                    </span>
                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                                        ${responses[currentQuestion.id] === option.score
                                            ? 'bg-earth-500 border-earth-500'
                                            : 'border-stone-300 group-hover:border-earth-300'
                                        }`}
                                    >
                                        {responses[currentQuestion.id] === option.score && (
                                            <CheckCircle2 size={14} className="text-white" />
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Minimal Nav / Info */}
                        <div className="mt-6 flex justify-center">
                            <p className="text-stone-400 text-sm">Select the option that best describes you.</p>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default AssessmentForm;
